<div
  class="page min-h-screen bg-gray-50 font-sans text-gray-900 selection:bg-indigo-100 selection:text-indigo-600"
  data-testid="assignment-landing-page"
>
  <!-- Navigation -->
  <nav class="fixed w-full z-50 bg-white border-b border-gray-100" data-testid="navbar">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center h-14 sm:h-16">
        <!-- Logo -->
        <div class="flex items-center gap-2">
          <div
            class="w-7 h-7 sm:w-8 sm:h-8 bg-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0"
          >
            <svg
              class="w-4 h-4 sm:w-5 sm:h-5 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <span class="text-lg sm:text-xl font-bold text-gray-900">JustTeach</span>
        </div>

        <!-- Desktop Nav -->
        <div class="hidden md:flex items-center space-x-6 lg:space-x-8" data-testid="desktop-nav">
          <a
            href="#how-it-works"
            (click)="scrollToSection($event, 'how-it-works')"
            class="text-gray-600 hover:text-indigo-600 font-medium transition cursor-pointer"
            data-testid="nav-how-it-works-link"
          >
            How it works
          </a>
          <a
            href="#features"
            (click)="scrollToSection($event, 'features')"
            class="text-gray-600 hover:text-indigo-600 font-medium transition cursor-pointer"
            data-testid="nav-features-link"
          >
            Features
          </a>
          <button
            (click)="goToAssignment('navbar_signup')"
            class="btn-primary px-4 lg:px-5 py-2 lg:py-2.5 rounded-lg font-semibold transition cursor-pointer text-sm lg:text-base"
            data-testid="nav-signup-btn"
          >
            Sign up free
          </button>
        </div>

        <!-- Mobile menu button -->
        <div class="md:hidden">
          <button
            (click)="toggleMenu()"
            class="text-gray-600 p-2 -mr-2 cursor-pointer"
            data-testid="mobile-menu-toggle"
          >
            @if (isMenuOpen()) {
              <lucide-icon [img]="icons.X" class="w-6 h-6"></lucide-icon>
            } @else {
              <lucide-icon [img]="icons.Menu" class="w-6 h-6"></lucide-icon>
            }
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Nav -->
    @if (isMenuOpen()) {
      <div
        class="md:hidden bg-white border-b border-gray-100 px-4 py-4 space-y-3 shadow-xl"
        data-testid="mobile-menu"
      >
        <a
          href="#how-it-works"
          (click)="scrollToSection($event, 'how-it-works'); closeMenu()"
          class="block text-gray-600 font-medium hover:text-indigo-600 transition px-2 py-2.5 rounded-lg hover:bg-gray-50"
          data-testid="mobile-nav-how-it-works-link"
        >
          How it works
        </a>
        <a
          href="#features"
          (click)="scrollToSection($event, 'features'); closeMenu()"
          class="block text-gray-600 font-medium hover:text-indigo-600 transition px-2 py-2.5 rounded-lg hover:bg-gray-50"
          data-testid="mobile-nav-features-link"
        >
          Features
        </a>
        <button
          (click)="goToAssignment('mobile_menu_signup'); closeMenu()"
          class="w-full text-center btn-primary py-3 rounded-lg font-semibold cursor-pointer mt-2"
          data-testid="mobile-nav-signup-btn"
        >
          Sign up free
        </button>
      </div>
    }
  </nav>

  <!-- Hero Section -->
  <section
    class="pt-20 pb-12 sm:pt-24 sm:pb-16 md:pt-28 lg:pt-36 lg:pb-24"
    data-testid="hero-section"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center max-w-4xl mx-auto">
        <!-- Main Heading -->
        <h1
          class="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 sm:mb-6 text-gray-900 leading-tight px-2 sm:px-0"
          data-testid="hero-heading"
        >
          AI-Powered Assessments:
          <span class="block text-gradient mt-1 sm:mt-2">
            From Instant Creation to Real-Time Grading
          </span>
        </h1>

        <!-- Subheading -->
        <p
          class="text-base sm:text-lg md:text-xl text-gray-600 mb-8 sm:mb-10 leading-relaxed max-w-3xl mx-auto px-2 sm:px-4 lg:px-0"
          data-testid="hero-subheading"
        >
          Create custom assessments in seconds, let AI grade responses and deliver explanations the
          moment students submit, so you can track progress live.
        </p>

        <!-- Assignment Creator Card -->
        <div
          class="bg-navy-deep rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 max-w-3xl mx-auto mb-4 sm:mb-6 shadow-xl"
          data-testid="assignment-creator-card"
        >
          <p
            class="text-white text-left font-semibold mb-3 sm:mb-4 text-sm sm:text-base"
            data-testid="card-title"
          >
            Try an assignment creator
          </p>

          <div
            class="flex flex-col sm:flex-row gap-2.5 sm:gap-3 md:gap-4"
            data-testid="creator-form"
          >
            <!-- Grade Select -->
            <select
              class="custom-select flex-1 bg-white text-gray-700 font-medium px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border-0 focus:ring-2 focus:ring-indigo-500 cursor-pointer text-sm sm:text-base"
              (change)="selectedGrade.set(+$any($event.target).value)"
              data-testid="grade-select"
            >
              <option value="" disabled [selected]="selectedGrade() === null">
                Select grade
              </option>
              @for (grade of grades(); track grade.value) {
                <option
                  [value]="grade.value"
                  [selected]="selectedGrade() === grade.value"
                >
                  {{ grade.label }}
                </option>
              }
            </select>

            <!-- Subject Select (Tier 1) -->
            <select
              class="custom-select flex-1 bg-white text-gray-700 font-medium px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border-0 focus:ring-2 focus:ring-indigo-500 cursor-pointer text-sm sm:text-base"
              (change)="selectedTier1SubjectId.set($any($event.target).value || null)"
              data-testid="subject-select"
            >
              <option value="" disabled [selected]="selectedTier1SubjectId() === null">
                Select subject
              </option>
              @for (subject of tier1Subjects(); track subject.id) {
                <option
                  [value]="subject.id"
                  [selected]="selectedTier1SubjectId() === subject.id"
                >
                  {{ subject.category }}
                </option>
              }
            </select>

            <!-- Generate Button -->
            <button
              (click)="onGenerate()"
              class="btn-accent px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap text-sm sm:text-base sm:flex-shrink-0"
              data-testid="generate-btn"
            >
              Generate
              <lucide-icon [img]="icons.ArrowRight" class="w-4 h-4"></lucide-icon>
            </button>
          </div>
        </div>

        <!-- Free disclaimer -->
        <div
          class="flex items-center justify-center gap-2 text-gray-500 text-xs sm:text-sm"
          data-testid="hero-disclaimer"
        >
          <lucide-icon
            [img]="icons.CheckCircle"
            class="w-4 h-4 text-green-500 flex-shrink-0"
          ></lucide-icon>
          <span>Free for teachers. No credit card required.</span>
        </div>
      </div>

      <!-- Social Proof Section -->
      <div
        class="mt-10 sm:mt-12 md:mt-16 pt-8 sm:pt-10 md:pt-12 border-t border-gray-200"
        data-testid="social-proof-section"
      >
        <div class="flex flex-col items-center justify-center gap-6 sm:gap-8 lg:flex-row lg:gap-16">
          <!-- Schools stat -->
          <div
            class="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left"
            data-testid="social-proof-schools"
          >
            <div class="avatar-stack flex justify-center">
              <img
                src="/images/landing/teacher1.png"
                alt="Teacher"
                class="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover"
              />
              <img
                src="/images/landing/teacher2.png"
                alt="Teacher"
                class="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover"
              />
              <img
                src="/images/landing/teacher3.png"
                alt="Teacher"
                class="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover"
              />
              <div
                class="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-green-100 text-green-700 text-[10px] sm:text-xs font-bold flex items-center justify-center border-2 border-white -ml-3"
              >
                +35k
              </div>
            </div>
            <p class="text-gray-700 font-medium text-sm sm:text-base">
              Supporting 35,000+ schools across America.
            </p>
          </div>

          <!-- Divider for mobile -->
          <div class="hidden sm:block lg:hidden w-16 h-px bg-gray-200"></div>

          <!-- Time saved stat -->
          <div
            class="flex items-center gap-3 text-center sm:text-left"
            data-testid="social-proof-time-saved"
          >
            <div
              class="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0"
            >
              <lucide-icon
                [img]="icons.Clock"
                class="w-4 h-4 sm:w-5 sm:h-5 text-green-600"
              ></lucide-icon>
            </div>
            <div class="text-left">
              <p class="text-green-600 font-bold text-base sm:text-lg">15 hours/week</p>
              <p class="text-gray-500 text-xs sm:text-sm max-w-[280px] sm:max-w-xs">
                Average time saved creating assignments, giving feedback, grading and checking
                assignments.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Easy as 1-2-3 Section -->
  <section
    id="how-it-works"
    class="py-16 sm:py-20 lg:py-24 bg-white"
    data-testid="easy-steps-section"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Section Title -->
      <h2
        class="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-center text-gray-900 mb-12 sm:mb-16"
        data-testid="easy-steps-heading"
      >
        Easy as 1-2-3
      </h2>

      <!-- Steps Grid -->
      <div
        class="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-12 relative"
        data-testid="easy-steps-grid"
      >
        <!-- Connecting line 1: between icon 1 and 2 (desktop only) -->
        <div class="hidden md:flex absolute top-9 left-[23%] w-[20%] items-center z-0">
          <div class="flex-1 border-t-2 border-dashed border-purple-300/60"></div>
          <div class="w-3 h-3 rounded-full bg-indigo-400/70 mx-2 flex-shrink-0"></div>
          <div class="flex-1 border-t-2 border-dashed border-indigo-300/60"></div>
        </div>
        <!-- Connecting line 2: between icon 2 and 3 (desktop only) -->
        <div class="hidden md:flex absolute top-9 left-[57%] w-[20%] items-center z-0">
          <div class="flex-1 border-t-2 border-dashed border-indigo-300/60"></div>
          <div class="w-3 h-3 rounded-full bg-blue-400/70 mx-2 flex-shrink-0"></div>
          <div class="flex-1 border-t-2 border-dashed border-blue-300/60"></div>
        </div>
        <!-- Step 1: Add Assignment -->
        <div class="flex items-center gap-4 text-left md:block md:text-center" data-testid="step-1">
          <div class="relative inline-block md:mb-6 flex-shrink-0 z-10">
            <div
              class="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center step-icon-purple shadow-lg"
            >
              <lucide-icon
                [img]="icons.Sparkles"
                class="w-7 h-7 sm:w-8 sm:h-8 icon-purple"
              ></lucide-icon>
            </div>
            <div
              class="absolute -top-1 -right-1 w-6 h-6 sm:w-7 sm:h-7 bg-gray-900 text-white text-xs sm:text-sm font-bold rounded-full flex items-center justify-center"
            >
              1
            </div>
          </div>
          <div>
            <h3
              class="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-3"
              data-testid="step-1-title"
            >
              Add Assignment
            </h3>
            <p
              class="text-gray-600 text-sm sm:text-base leading-relaxed md:max-w-xs md:mx-auto"
              data-testid="step-1-desc"
            >
              Generate with AI, upload a PDF, or create from scratch in seconds.
            </p>
          </div>
        </div>

        <!-- Step 2: Invite Students -->
        <div class="flex items-center gap-4 text-left md:block md:text-center" data-testid="step-2">
          <div class="relative inline-block md:mb-6 flex-shrink-0 z-10">
            <div
              class="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center step-icon-violet shadow-lg"
            >
              <lucide-icon
                [img]="icons.Users"
                class="w-7 h-7 sm:w-8 sm:h-8 icon-violet"
              ></lucide-icon>
            </div>
            <div
              class="absolute -top-1 -right-1 w-6 h-6 sm:w-7 sm:h-7 bg-gray-900 text-white text-xs sm:text-sm font-bold rounded-full flex items-center justify-center"
            >
              2
            </div>
          </div>
          <div>
            <h3
              class="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-3"
              data-testid="step-2-title"
            >
              Invite Students
            </h3>
            <p
              class="text-gray-600 text-sm sm:text-base leading-relaxed md:max-w-xs md:mx-auto"
              data-testid="step-2-desc"
            >
              Students get instant feedback and AI-powered analysis to bridge learning gaps.
            </p>
          </div>
        </div>

        <!-- Step 3: View Insights -->
        <div class="flex items-center gap-4 text-left md:block md:text-center" data-testid="step-3">
          <div class="relative inline-block md:mb-6 flex-shrink-0 z-10">
            <div
              class="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center step-icon-blue shadow-lg"
            >
              <lucide-icon
                [img]="icons.BarChart3"
                class="w-7 h-7 sm:w-8 sm:h-8 icon-blue"
              ></lucide-icon>
            </div>
            <div
              class="absolute -top-1 -right-1 w-6 h-6 sm:w-7 sm:h-7 bg-gray-900 text-white text-xs sm:text-sm font-bold rounded-full flex items-center justify-center"
            >
              3
            </div>
          </div>
          <div>
            <h3
              class="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-3"
              data-testid="step-3-title"
            >
              View Insights
            </h3>
            <p
              class="text-gray-600 text-sm sm:text-base leading-relaxed md:max-w-xs md:mx-auto"
              data-testid="step-3-desc"
            >
              Gain deep insights into strengths, growth areas, and academic integrity.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Stop Working For Free Section -->
  <section
    id="features"
    class="py-16 sm:py-20 lg:py-24 bg-gray-50"
    data-testid="comparison-section"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Section Title -->
      <div class="text-center mb-10 sm:mb-14">
        <h2
          class="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3 sm:mb-4"
          data-testid="comparison-heading"
        >
          Stop Working For Free
        </h2>
        <p
          class="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto"
          data-testid="comparison-subheading"
        >
          The math is simple. The burnout is real. Reclaim your nights and weekends.
        </p>
      </div>

      <!-- Comparison Cards -->
      <div
        class="flex flex-col lg:flex-row items-stretch gap-6 lg:gap-8 max-w-5xl mx-auto"
        data-testid="comparison-cards"
      >
        <!-- The Old Way Card -->
        <div
          class="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-100 lg:w-[48%] lg:self-center"
          data-testid="old-way-card"
        >
          <div
            class="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold uppercase tracking-wider rounded-full mb-6"
          >
            The Old Way
          </div>

          <div class="space-y-5 sm:space-y-6">
            <!-- Item 1 -->
            <div class="flex items-start gap-3 sm:gap-4">
              <div
                class="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0"
              >
                <lucide-icon
                  [img]="icons.Moon"
                  class="w-4 h-4 sm:w-5 sm:h-5 text-orange-500"
                ></lucide-icon>
              </div>
              <div>
                <h4 class="font-bold text-gray-900 text-sm sm:text-base mb-1">
                  Sunday Night Dread
                </h4>
                <p class="text-gray-500 text-xs sm:text-sm leading-relaxed">
                  Spend your Sunday grading 30+ essays instead of being with family.
                </p>
              </div>
            </div>

            <!-- Item 2 -->
            <div class="flex items-start gap-3 sm:gap-4">
              <div
                class="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0"
              >
                <lucide-icon
                  [img]="icons.Clock3"
                  class="w-4 h-4 sm:w-5 sm:h-5 text-red-500"
                ></lucide-icon>
              </div>
              <div>
                <h4 class="font-bold text-gray-900 text-sm sm:text-base mb-1">Delayed Results</h4>
                <p class="text-gray-500 text-xs sm:text-sm leading-relaxed">
                  Papers are returned days later, long after students have moved on.
                </p>
              </div>
            </div>

            <!-- Item 3 -->
            <div class="flex items-start gap-3 sm:gap-4">
              <div
                class="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0"
              >
                <lucide-icon
                  [img]="icons.FileEdit"
                  class="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600"
                ></lucide-icon>
              </div>
              <div>
                <h4 class="font-bold text-gray-900 text-sm sm:text-base mb-1">
                  Manual Content Drafting
                </h4>
                <p class="text-gray-500 text-xs sm:text-sm leading-relaxed">
                  Wasting hours typing out essays, prompts, and rubrics from scratch for every
                  single class.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- The JustTeach Way Card -->
        <div
          class="bg-indigo-600 rounded-2xl p-6 sm:p-8 shadow-xl lg:w-[52%]"
          data-testid="justteach-way-card"
        >
          <div
            class="inline-block px-3 py-1 bg-white text-indigo-600 text-xs font-bold uppercase tracking-wider rounded-full mb-6"
          >
            The JustTeach Way
          </div>

          <div class="space-y-5 sm:space-y-6">
            <!-- Item 1 -->
            <div class="flex items-start gap-3 sm:gap-4">
              <div
                class="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-yellow-300/80 flex items-center justify-center flex-shrink-0"
              >
                <lucide-icon
                  [img]="icons.Zap"
                  class="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 icon-filled"
                ></lucide-icon>
              </div>
              <div>
                <h4 class="font-bold text-white text-sm sm:text-base mb-1">Instant Auto-Grading</h4>
                <p class="text-indigo-100 text-xs sm:text-sm leading-relaxed">
                  Grade 30+ assignments in 0.5 seconds. Scores are ready before class ends.
                </p>
              </div>
            </div>

            <!-- Item 2 -->
            <div class="flex items-start gap-3 sm:gap-4">
              <div
                class="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-green-300/80 flex items-center justify-center flex-shrink-0"
              >
                <lucide-icon
                  [img]="icons.MessageCircle"
                  class="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 icon-filled"
                ></lucide-icon>
              </div>
              <div>
                <h4 class="font-bold text-white text-sm sm:text-base mb-1">Real-Time Coaching</h4>
                <p class="text-indigo-100 text-xs sm:text-sm leading-relaxed">
                  Every student gets instant, personalized feedback explaining their mistakes.
                </p>
              </div>
            </div>

            <!-- Item 3 -->
            <div class="flex items-start gap-3 sm:gap-4">
              <div
                class="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-purple-300/80 flex items-center justify-center flex-shrink-0"
              >
                <lucide-icon
                  [img]="icons.Wand2"
                  class="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 icon-filled"
                ></lucide-icon>
              </div>
              <div>
                <h4 class="font-bold text-white text-sm sm:text-base mb-1">
                  1-Minute AI Generation
                </h4>
                <p class="text-indigo-100 text-xs sm:text-sm leading-relaxed">
                  Instantly create essays, short answers, and open-ended questions custom-built for
                  your lesson.
                </p>
              </div>
            </div>
          </div>

          <!-- Footer with stats and CTA -->
          <div
            class="mt-8 pt-6 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-4"
          >
            <div class="text-center sm:text-left">
              <p class="text-2xl sm:text-3xl font-extrabold text-white italic">15 hrs</p>
              <p class="text-indigo-200 text-xs uppercase tracking-wider">Time Saved Weekly</p>
            </div>
            <button
              (click)="goToAssignment('comparison_signup')"
              class="w-full sm:w-auto bg-white text-gray-900 px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-gray-100 transition cursor-pointer text-sm sm:text-base"
              data-testid="comparison-cta-btn"
            >
              Sign Up Free
              <lucide-icon [img]="icons.ArrowRight" class="w-4 h-4"></lucide-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Complete Assessment Toolkit Section -->
  <section class="py-16 sm:py-20 lg:py-24 bg-white" data-testid="toolkit-section">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Section Title -->
      <div class="text-center mb-10 sm:mb-14">
        <h2
          class="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3 sm:mb-4 uppercase tracking-wide"
          data-testid="toolkit-heading"
        >
          Complete Assessment Toolkit
        </h2>
        <p
          class="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto"
          data-testid="toolkit-subheading"
        >
          From essays to multiple choice, JustTeach handles it all while keeping your data safe.
        </p>
      </div>

      <!-- Features Grid -->
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
        data-testid="toolkit-grid"
      >
        <!-- Feature 1: Flexible Formats -->
        <div class="bg-gray-50 rounded-2xl p-5 sm:p-6" data-testid="toolkit-feature-1">
          <div class="flex items-center gap-3 mb-3">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-fuchsia-500 flex items-center justify-center flex-shrink-0"
            >
              <lucide-icon
                [img]="icons.FileText"
                class="w-5 h-5 sm:w-6 sm:h-6 text-white icon-filled"
              ></lucide-icon>
            </div>
            <h3 class="font-bold text-gray-900 text-base sm:text-lg">Flexible Formats</h3>
          </div>
          <p class="text-gray-500 text-sm sm:text-base leading-relaxed">
            Multiple choice, open-ended questions, and essays. Add images or video to make
            assignments interactive.
          </p>
        </div>

        <!-- Feature 2: Auto-Rubrics -->
        <div class="bg-gray-50 rounded-2xl p-5 sm:p-6" data-testid="toolkit-feature-2">
          <div class="flex items-center gap-3 mb-3">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-indigo-500 flex items-center justify-center flex-shrink-0"
            >
              <lucide-icon
                [img]="icons.ClipboardList"
                class="w-5 h-5 sm:w-6 sm:h-6 text-white icon-filled"
              ></lucide-icon>
            </div>
            <h3 class="font-bold text-gray-900 text-base sm:text-lg">Auto-Rubrics</h3>
          </div>
          <p class="text-gray-500 text-sm sm:text-base leading-relaxed">
            Our AI generates detailed rubrics for every assignment instantly. Consistent, fair
            grading in seconds.
          </p>
        </div>

        <!-- Feature 3: Academic Integrity -->
        <div class="bg-gray-50 rounded-2xl p-5 sm:p-6" data-testid="toolkit-feature-3">
          <div class="flex items-center gap-3 mb-3">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0"
            >
              <lucide-icon
                [img]="icons.ShieldCheck"
                class="w-5 h-5 sm:w-6 sm:h-6 text-white icon-filled"
              ></lucide-icon>
            </div>
            <h3 class="font-bold text-gray-900 text-base sm:text-lg">Academic Integrity</h3>
          </div>
          <p class="text-gray-500 text-sm sm:text-base leading-relaxed">
            No ready-made answer keys online. Questions are generated dynamically to ensure
            authentic student work.
          </p>
        </div>

        <!-- Feature 4: Smart Deadlines -->
        <div class="bg-gray-50 rounded-2xl p-5 sm:p-6" data-testid="toolkit-feature-4">
          <div class="flex items-center gap-3 mb-3">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0"
            >
              <lucide-icon
                [img]="icons.Timer"
                class="w-5 h-5 sm:w-6 sm:h-6 text-white icon-filled"
              ></lucide-icon>
            </div>
            <h3 class="font-bold text-gray-900 text-base sm:text-lg">Smart Deadlines</h3>
          </div>
          <p class="text-gray-500 text-sm sm:text-base leading-relaxed">
            Set strict due dates or allow late submissions. You control the pace, we handle the
            tracking.
          </p>
        </div>

        <!-- Feature 5: Multimedia Support -->
        <div class="bg-gray-50 rounded-2xl p-5 sm:p-6" data-testid="toolkit-feature-5">
          <div class="flex items-center gap-3 mb-3">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-rose-500 flex items-center justify-center flex-shrink-0"
            >
              <lucide-icon
                [img]="icons.Image"
                class="w-5 h-5 sm:w-6 sm:h-6 text-white icon-filled"
              ></lucide-icon>
            </div>
            <h3 class="font-bold text-gray-900 text-base sm:text-lg">Multimedia Support</h3>
          </div>
          <p class="text-gray-500 text-sm sm:text-base leading-relaxed">
            Enhance learning by embedding diagrams, photos, and videos directly into your assessment
            questions.
          </p>
        </div>

        <!-- Feature 6: Instant AI Feedback -->
        <div class="bg-gray-50 rounded-2xl p-5 sm:p-6" data-testid="toolkit-feature-6">
          <div class="flex items-center gap-3 mb-3">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-indigo-500 flex items-center justify-center flex-shrink-0"
            >
              <lucide-icon
                [img]="icons.MessageSquareText"
                class="w-5 h-5 sm:w-6 sm:h-6 text-white icon-filled"
              ></lucide-icon>
            </div>
            <h3 class="font-bold text-gray-900 text-base sm:text-lg">Instant AI Feedback</h3>
          </div>
          <p class="text-gray-500 text-sm sm:text-base leading-relaxed">
            Students receive personalized, real-time coaching for every response. Help them learn
            from mistakes instantly.
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- Works with your entire curriculum Section -->
  <section class="py-16 sm:py-20 lg:py-24 bg-gray-50" data-testid="curriculum-section">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Section Title -->
      <div class="text-center mb-10 sm:mb-12">
        <h2
          class="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3 sm:mb-4"
          data-testid="curriculum-heading"
        >
          Works with your entire curriculum
        </h2>
        <p
          class="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto"
          data-testid="curriculum-subheading"
        >
          From Algebra to US History, JustTeach supports every subject you teach.
        </p>
      </div>

      <!-- Subjects Grid -->
      <div
        class="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto mb-8"
        data-testid="curriculum-grid"
      >
        <!-- Mathematics -->
        <div
          class="bg-white rounded-xl px-4 py-3 sm:px-5 sm:py-4 lg:px-6 flex items-center gap-3 shadow-sm"
        >
          <div
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0"
          >
            <lucide-icon
              [img]="icons.Calculator"
              class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600"
            ></lucide-icon>
          </div>
          <span class="text-gray-700 font-medium text-sm sm:text-base lg:whitespace-nowrap"
            >Mathematics</span
          >
        </div>

        <!-- English ELA -->
        <div
          class="bg-white rounded-xl px-4 py-3 sm:px-5 sm:py-4 lg:px-6 flex items-center gap-3 shadow-sm"
        >
          <div
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0"
          >
            <lucide-icon
              [img]="icons.BookOpen"
              class="w-4 h-4 sm:w-5 sm:h-5 text-amber-600"
            ></lucide-icon>
          </div>
          <span class="text-gray-700 font-medium text-sm sm:text-base lg:whitespace-nowrap"
            >English ELA</span
          >
        </div>

        <!-- Science -->
        <div
          class="bg-white rounded-xl px-4 py-3 sm:px-5 sm:py-4 lg:px-6 flex items-center gap-3 shadow-sm"
        >
          <div
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0"
          >
            <lucide-icon
              [img]="icons.FlaskConical"
              class="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600"
            ></lucide-icon>
          </div>
          <span class="text-gray-700 font-medium text-sm sm:text-base lg:whitespace-nowrap"
            >Science</span
          >
        </div>

        <!-- Social Studies -->
        <div
          class="bg-white rounded-xl px-4 py-3 sm:px-5 sm:py-4 lg:px-6 flex items-center gap-3 shadow-sm"
        >
          <div
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0"
          >
            <lucide-icon
              [img]="icons.Globe"
              class="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600"
            ></lucide-icon>
          </div>
          <span class="text-gray-700 font-medium text-sm sm:text-base lg:whitespace-nowrap"
            >Social Studies</span
          >
        </div>

        <!-- US History -->
        <div
          class="bg-white rounded-xl px-4 py-3 sm:px-5 sm:py-4 lg:px-6 flex items-center gap-3 shadow-sm"
        >
          <div
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0"
          >
            <lucide-icon
              [img]="icons.Landmark"
              class="w-4 h-4 sm:w-5 sm:h-5 text-red-600"
            ></lucide-icon>
          </div>
          <span class="text-gray-700 font-medium text-sm sm:text-base lg:whitespace-nowrap"
            >US History</span
          >
        </div>

        <!-- Computer Science -->
        <div
          class="bg-white rounded-xl px-4 py-3 sm:px-5 sm:py-4 lg:px-6 flex items-center gap-3 shadow-sm"
        >
          <div
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"
          >
            <lucide-icon
              [img]="icons.Terminal"
              class="w-4 h-4 sm:w-5 sm:h-5 text-gray-600"
            ></lucide-icon>
          </div>
          <span class="text-gray-700 font-medium text-sm sm:text-base lg:whitespace-nowrap"
            >Computer Science</span
          >
        </div>

        <!-- World Languages -->
        <div
          class="bg-white rounded-xl px-4 py-3 sm:px-5 sm:py-4 lg:px-6 flex items-center gap-3 shadow-sm"
        >
          <div
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0"
          >
            <lucide-icon
              [img]="icons.Languages"
              class="w-4 h-4 sm:w-5 sm:h-5 text-rose-600"
            ></lucide-icon>
          </div>
          <span class="text-gray-700 font-medium text-sm sm:text-base lg:whitespace-nowrap"
            >World Languages</span
          >
        </div>

        <!-- Arts & Music -->
        <div
          class="bg-white rounded-xl px-4 py-3 sm:px-5 sm:py-4 lg:px-6 flex items-center gap-3 shadow-sm"
        >
          <div
            class="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0"
          >
            <lucide-icon
              [img]="icons.Palette"
              class="w-4 h-4 sm:w-5 sm:h-5 text-violet-600"
            ></lucide-icon>
          </div>
          <span class="text-gray-700 font-medium text-sm sm:text-base lg:whitespace-nowrap"
            >Arts & Music</span
          >
        </div>
      </div>

      <!-- View all link -->
      <div class="text-center">
        <a
          (click)="goToAssignment('subjects_link')"
          class="inline-flex items-center gap-2 text-green-600 font-semibold hover:text-green-700 transition text-sm sm:text-base"
          data-testid="view-all-subjects-link"
        >
          View all supported subjects
          <lucide-icon [img]="icons.ArrowRight" class="w-4 h-4"></lucide-icon>
        </a>
      </div>
    </div>
  </section>

  <!-- Privacy Banner Section -->
  <section class="py-8 sm:py-10 bg-white" data-testid="privacy-section">
    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div
        class="bg-green-50 border border-green-100 rounded-2xl p-5 sm:p-6 lg:p-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6"
      >
        <!-- Icon -->
        <div
          class="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0"
        >
          <lucide-icon
            [img]="icons.ShieldCheck"
            class="w-6 h-6 sm:w-7 sm:h-7 text-green-600"
          ></lucide-icon>
        </div>

        <!-- Content -->
        <div class="flex-1">
          <h3
            class="font-bold text-gray-900 text-lg sm:text-xl mb-1 sm:mb-2"
            data-testid="privacy-heading"
          >
            We take privacy seriously
          </h3>
          <p
            class="text-gray-600 text-sm sm:text-base leading-relaxed"
            data-testid="privacy-description"
          >
            JustTeach complies with
            <span class="font-semibold">FERPA</span>, <span class="font-semibold">COPPA</span>, and
            state data privacy laws. We are a proud signatory of the Student Privacy Pledge. Your
            student data is safe, secure, and never sold.
          </p>
        </div>

        <!-- Badges -->
        <div class="flex gap-2 flex-shrink-0">
          <span
            class="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs sm:text-sm font-semibold text-gray-700"
            >FERPA</span
          >
          <span
            class="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs sm:text-sm font-semibold text-gray-700"
            >COPPA</span
          >
        </div>
      </div>
    </div>
  </section>

  <!-- Testimonials Section -->
  <section class="py-16 sm:py-20 lg:py-24 bg-gray-50" data-testid="testimonials-section">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Section Title -->
      <h2
        class="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-center text-gray-900 mb-10 sm:mb-14"
        data-testid="testimonials-heading"
      >
        Teachers are getting their weekends back
      </h2>

      <!-- Testimonials Grid -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials-grid">
        <!-- Testimonial 1 -->
        <div class="bg-gray-50 rounded-2xl p-5 sm:p-6 flex flex-col" data-testid="testimonial-1">
          <!-- Stars -->
          <div class="flex gap-1 mb-4">
            @for (i of [1, 2, 3, 4, 5]; track i) {
              <lucide-icon
                [img]="icons.Star"
                class="w-4 h-4 text-yellow-400 fill-yellow-400"
              ></lucide-icon>
            }
          </div>

          <!-- Quote -->
          <p class="text-gray-600 text-sm sm:text-base leading-relaxed mb-6 flex-1 italic">
            "I used to spend every Sunday grading history essays. Now JustTeach does the heavy
            lifting, and I focus on lesson planning."
          </p>

          <!-- Author -->
          <div class="flex items-center justify-between">
            <div>
              <p class="font-bold text-gray-900 text-sm sm:text-base">Sarah J.</p>
              <p class="text-gray-500 text-xs sm:text-sm">High School History Teacher</p>
            </div>
            <span
              class="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full whitespace-nowrap"
              >Saved 8hrs/week</span
            >
          </div>
        </div>

        <!-- Testimonial 2 -->
        <div class="bg-gray-50 rounded-2xl p-5 sm:p-6 flex flex-col" data-testid="testimonial-2">
          <!-- Stars -->
          <div class="flex gap-1 mb-4">
            @for (i of [1, 2, 3, 4, 5]; track i) {
              <lucide-icon
                [img]="icons.Star"
                class="w-4 h-4 text-yellow-400 fill-yellow-400"
              ></lucide-icon>
            }
          </div>

          <!-- Quote -->
          <p class="text-gray-600 text-sm sm:text-base leading-relaxed mb-6 flex-1 italic">
            "The students actually prefer it. They get feedback instantly instead of waiting a week
            for me to return papers."
          </p>

          <!-- Author -->
          <div class="flex items-center justify-between">
            <div>
              <p class="font-bold text-gray-900 text-sm sm:text-base">Mike T.</p>
              <p class="text-gray-500 text-xs sm:text-sm">AP Science Instructor</p>
            </div>
            <span
              class="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full whitespace-nowrap"
              >Faster feedback</span
            >
          </div>
        </div>

        <!-- Testimonial 3 -->
        <div class="bg-gray-50 rounded-2xl p-5 sm:p-6 flex flex-col" data-testid="testimonial-3">
          <!-- Stars -->
          <div class="flex gap-1 mb-4">
            @for (i of [1, 2, 3, 4, 5]; track i) {
              <lucide-icon
                [img]="icons.Star"
                class="w-4 h-4 text-yellow-400 fill-yellow-400"
              ></lucide-icon>
            }
          </div>

          <!-- Quote -->
          <p class="text-gray-600 text-sm sm:text-base leading-relaxed mb-6 flex-1 italic">
            "It's not just a quiz generator. It's a complete grading assistant. Best free tool I've
            found this year."
          </p>

          <!-- Author -->
          <div class="flex items-center justify-between">
            <div>
              <p class="font-bold text-gray-900 text-sm sm:text-base">Elena R.</p>
              <p class="text-gray-500 text-xs sm:text-sm">Middle School Math</p>
            </div>
            <span
              class="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full whitespace-nowrap"
              >Easy to use</span
            >
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Final CTA Section -->
  <section class="py-12 sm:py-16 lg:py-20 bg-white" data-testid="final-cta-section">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="bg-indigo-600 rounded-3xl p-8 sm:p-12 lg:p-16 text-center shadow-xl">
        <h2
          class="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white uppercase tracking-wide mb-4"
          data-testid="final-cta-heading"
        >
          Save Your Time With AI Assessments
        </h2>
        <p
          class="text-indigo-100 text-base sm:text-lg mb-8 max-w-2xl mx-auto"
          data-testid="final-cta-description"
        >
          Create assessments in seconds with instant AI feedback and real-time student insights.
        </p>
        <button
          (click)="goToAssignment('final_cta')"
          class="inline-flex items-center gap-2 bg-white text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-sm sm:text-base hover:bg-gray-100 transition cursor-pointer uppercase tracking-wide"
          data-testid="final-cta-btn"
        >
          Get Started For Free
          <lucide-icon [img]="icons.ArrowRight" class="w-4 h-4"></lucide-icon>
        </button>
      </div>
    </div>
  </section>

  <!-- Footer -->
  <footer class="py-8 sm:py-10 bg-gray-100 border-t border-gray-200" data-testid="footer">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
        <!-- Logo -->
        <div class="flex items-center gap-2">
          <div class="w-7 h-7 bg-indigo-500 rounded-lg flex items-center justify-center">
            <svg
              class="w-4 h-4 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <span class="text-base font-bold text-gray-900">JustTeach</span>
        </div>

        <!-- Links -->
        <div class="flex items-center gap-6 sm:gap-8 text-sm font-medium text-gray-600">
          <a
            href="/privacy-policy"
            class="hover:text-gray-900 transition"
            data-testid="footer-privacy-link"
            >PRIVACY</a
          >
          <a
            href="/terms-of-use"
            class="hover:text-gray-900 transition"
            data-testid="footer-terms-link"
            >TERMS</a
          >
          <a
            href="mailto:justteachsupport@justteach.ai"
            class="hover:text-gray-900 transition"
            data-testid="footer-support-link"
            >SUPPORT</a
          >
        </div>

        <!-- Copyright -->
        <p class="text-sm text-gray-500" data-testid="footer-copyright">&copy; 2026 JustTeach AI</p>
      </div>
    </div>
  </footer>
</div>
